import { useCallback, useMemo, useState } from 'react';
import { IconDice, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import FormField from '../components/ui/FormField';
import ErrorMessage from '../components/ui/ErrorMessage';
import { formatDateTime } from '../utils/formatDate';
import { generatePassword } from '../utils/password';
import { usePaginatedList } from '../hooks/usePaginatedList';
import './CrudPage.scss';

const CUSTOMER_FIELDS = [
    { key: 'login', label: 'Логин', required: true },
    { key: 'password', label: 'Пароль', secret: true },
    { key: 'referral_code', label: 'Реферальный код', hint: 'Свой код клиента — им делятся с друзьями' },
    { key: 'referred_by_code', label: 'Код реферера', hint: 'Реф. код пользователя, который пригласил этого клиента' },
    { key: 'referral_points', label: 'Реф. баллы', type: 'number' },
    { key: 'admin', label: 'Админ', type: 'checkbox' },
];

function emptyForm() {
    return {
        login: '',
        password: '',
        referral_code: '',
        referred_by_code: '',
        referral_points: '0',
        admin: false,
    };
}

export default function CustomersPage() {
    const fetchList = useCallback(
        (params) => api.listResource('customers', params),
        [],
    );
    const {
        items, total, offset, limit, loading, error, setOffset, reload,
    } = usePaginatedList(fetchList);

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            login: row.login || '',
            password: '',
            referral_code: row.referral_code || '',
            referred_by_code: row.referred_by_code || '',
            referral_points: String(row.referral_points ?? 0),
            admin: Boolean(row.admin),
        });
        setFormError('');
        setModalOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setFormError('');
        try {
            if (editing) {
                const patch = {};
                if (form.login !== editing.login) patch.login = form.login;
                if (form.referral_code !== (editing.referral_code || '')) patch.referral_code = form.referral_code;
                if (form.referred_by_code !== (editing.referred_by_code || '')) {
                    patch.referred_by_code = form.referred_by_code || null;
                }
                if (Number(form.referral_points) !== editing.referral_points) {
                    patch.referral_points = Number(form.referral_points);
                }
                if (Boolean(form.admin) !== Boolean(editing.admin)) patch.admin = form.admin;
                if (form.password) patch.password = form.password;
                await api.updateResource('customers', editing.id, patch);
            } else {
                if (!form.password) throw new Error('Пароль обязателен при создании');
                await api.createResource('customers', {
                    login: form.login,
                    password: form.password,
                    referral_code: form.referral_code || undefined,
                    referred_by_code: form.referred_by_code || undefined,
                    referral_points: Number(form.referral_points) || 0,
                    admin: form.admin,
                });
            }
            setModalOpen(false);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteResource('customers', deleteTarget.id);
            setDeleteTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка удаления');
        } finally {
            setDeleting(false);
        }
    };

    const columns = useMemo(() => [
        { key: 'id', title: 'ID', width: 60 },
        { key: 'login', title: 'Логин' },
        { key: 'referral_code', title: 'Реф. код' },
        { key: 'referral_points', title: 'Баллы' },
        {
            key: 'admin',
            title: 'Админ',
            render: (v) => (v ? 'Да' : '—'),
        },
        {
            key: 'created_at',
            title: 'Создан',
            render: (v) => formatDateTime(v),
        },
        {
            key: 'actions',
            title: '',
            render: (_, row) => (
                <div className="cell-actions">
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => openEdit(row)}>
                        <IconEdit size={16} />
                    </button>
                    <button type="button" className="btn btn--danger btn--sm" onClick={() => setDeleteTarget(row)}>
                        <IconTrash size={16} />
                    </button>
                </div>
            ),
        },
    ], []);

    return (
        <div className="crud-page">
            <PageHeader
                title="Клиенты"
                subtitle="Учётные записи пользователей"
                actions={(
                    <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                        <IconPlus size={16} />
                        Добавить
                    </button>
                )}
            />

            <ErrorMessage message={error} onRetry={reload} />

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                pagination={{ total, limit, offset, onPageChange: setOffset }}
            />

            <Modal
                open={modalOpen}
                title={editing ? `Клиент #${editing.id}` : 'Новый клиент'}
                onClose={() => setModalOpen(false)}
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setModalOpen(false)}>
                            Отмена
                        </button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleSave} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <div className="form-grid">
                    {CUSTOMER_FIELDS.map(({ key, label, type = 'text', secret, hint }) => (
                    type === 'checkbox' ? (
                        <label key={key} className="form-check">
                            <input
                                type="checkbox"
                                checked={form.admin}
                                onChange={(e) => setForm((p) => ({ ...p, admin: e.target.checked }))}
                            />
                            {label}
                        </label>
                    ) : (
                        <FormField
                            key={key}
                            label={label}
                            hint={editing && secret ? 'Оставьте пустым, чтобы не менять' : hint}
                            type={type === 'number' ? 'number' : 'text'}
                            value={form[key]}
                            onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                            autoComplete={secret ? 'new-password' : undefined}
                            rightElement={secret ? (
                                <button
                                    type="button"
                                    className="ui-form-field__action-btn"
                                    onClick={() => setForm((p) => ({
                                        ...p,
                                        password: generatePassword(10),
                                    }))}
                                    aria-label="Сгенерировать пароль"
                                >
                                    <IconDice size={18} stroke={1.5} />
                                </button>
                            ) : undefined}
                        />
                    )
                ))}
                </div>
            </Modal>

            <ConfirmModal
                open={Boolean(deleteTarget)}
                title="Удалить клиента?"
                message={`Клиент ${deleteTarget?.login || ''} будет удалён.`}
                confirmLabel="Удалить"
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}
